import {
	CommunityPostImageSaveModel,
	ExistingCommunityPostImageSaveModel,
	CommunityPostImageSaveModelKind,
	NewCommunityPostImageSaveModel,
	CommunityPostSaveModel,
	CommunityPostPollAnswerSaveModel,
	CommunityPostPollSaveModel
} from "client/Api/Communities/communityPosts";
import { Base64FileUriUploadWithMetadataModel } from "client/Misc/commonTypes";
import { getClientId, ObjectWithClientId } from "client/Misc/clientId";
import {
	createValidationConfiguration,
	InputValidationResult,
	InputValidationResultKind,
	IValidationConfiguration
} from "client/Validation";
import {
	doesNotExceedLength,
	hasMaximumEntries,
	hasMaxSize,
	hasMinimumEntries,
	inline,
	isNotEmpty,
	isNotNullNorUndefined,
	isSupportedFileType
} from "client/Validation/rules";
import { createUrlRelativeToEnvironment } from "client_legacy/ndaw/navigator";
import {
	EMPTY_ARRAY,
	isNil,
	isNotNil
} from "client/Misc/utils";
import { never } from "client_legacy/ndaw/errors";
import { ICollectionValidatorBuilder } from "client/Validation/builder";
import { localize } from "client_legacy/ndaw/culture";
import { Guid } from "client_legacy/ndaw/guid";
import { MAX_LINKED_DOCUMENTS_COUNT } from "./NewPostComposer";
import { DocumentStoreType } from "client/Api/Documents/common";
import { LinkedDocumentFormData } from "../../common";
import { HubLinkedDocumentSaveModel } from "client/Api/Hub/common.composer";

export type CommunityPollAnswerFormData = ObjectWithClientId<CommunityPostPollAnswerSaveModel>;

export type CommunityPollFormData = OverrideProperties<
	CommunityPostPollSaveModel,
	{
		answers: readonly CommunityPollAnswerFormData[];
	}
>;

export type PostComposerFormData = OverrideProperties<
	CommunityPostSaveModel,
	{
		images: readonly ObjectWithClientId<CommunityPostImageFormData>[];
		poll: OptionalModel<CommunityPollFormData | null>;
		linkedDocuments: readonly ObjectWithClientId<LinkedDocumentFormData>[];
	}
> & {
	communityId: number | null;
	id: number | null;
};

export const enum CommunityPostImageFormModelKind {
	New = CommunityPostImageSaveModelKind.New,
	Existing = CommunityPostImageSaveModelKind.Existing,
	Loading = 3
}

export type PendingCommunityPostImageFormData = {
	kind: CommunityPostImageFormModelKind.Loading;
	cancel: () => void;
	id: Guid;
};

export type NewCommunityPostImageFormData = OverrideProperties<
	NewCommunityPostImageSaveModel,
	{
		image: Base64FileUriUploadWithMetadataModel;
		kind: CommunityPostImageFormModelKind.New;
	}
>;

export type ExistingCommunityPostImageFormData = OverrideProperties<
	ExistingCommunityPostImageSaveModel,
	{
		kind: CommunityPostImageFormModelKind.Existing;
	}
>;

export type CommunityPostImageFormData =
	| NewCommunityPostImageFormData
	| ExistingCommunityPostImageFormData
	| PendingCommunityPostImageFormData;

function convertImagesToSaveModel(
	images: readonly ObjectWithClientId<CommunityPostImageFormData>[]
): readonly CommunityPostImageSaveModel[] {
	return images
		.filter(x => x.kind !== CommunityPostImageFormModelKind.Loading)
		.map(
			(x: NewCommunityPostImageFormData | ExistingCommunityPostImageFormData): CommunityPostImageSaveModel => {
				switch (x.kind) {
					case CommunityPostImageFormModelKind.New:
						return {
							kind: CommunityPostImageSaveModelKind.New,
							image: x.image
						};
					case CommunityPostImageFormModelKind.Existing:
						return {
							kind: CommunityPostImageSaveModelKind.Existing,
							id: x.id
						};
					default:
						return never(x);
				}
			}
		);
}

export function convertToCommunityPostSaveModel(
	form: PostComposerFormData
): CommunityPostSaveModel {
	return {
		message: form.message,
		poll: form.poll,
		images: convertImagesToSaveModel(form.images),
		linkedDocuments: form
			.linkedDocuments
			.map(convertLinkedDocumentsToSaveModel)
	};
}

function convertLinkedDocumentsToSaveModel(
	item: LinkedDocumentFormData
): HubLinkedDocumentSaveModel {
	switch (item.documentStoreType) {
		case DocumentStoreType.SharePoint:
			return {
				documentStoreType: DocumentStoreType.SharePoint,
				itemId: item.itemId,
				location: item.location,
				name: item.name
			};
		case DocumentStoreType.WebDav:
			return {
				documentStoreType: DocumentStoreType.WebDav,
				fileId: item.fileId,
				serverId: item.serverId,
				name: item.name
			};
		default:
			never(item);
	}
}

export const MAX_IMAGES_PER_POST: number = 10;

export const EMPTY_POST_FORM_DATA: PostComposerFormData = {
	communityId: null,
	id: null,
	images: EMPTY_ARRAY,
	poll: { isSet: true, value: null },
	message: "",
	linkedDocuments: EMPTY_ARRAY
};

export function getCommunityPostImageUrl(imageId: string): string {
	return createUrlRelativeToEnvironment(
		`/Hub/Communities/Posts/Image/?imageId=${encodeURIComponent(imageId)}`
	);
}

export const MAX_POLL_ANSWERS_COUNT: number = 10;
export const MIN_POLL_ANSWERS_COUNT: number = 2;

export type PostComposerValidationOptions = {
	supportedImageTypes: readonly string[];
	maxImageSize: number;
};

function createValidationConfigurationForPost(
	options: PostComposerValidationOptions
): IValidationConfiguration<PostComposerFormData> {
	const validationConfiguration: IValidationConfiguration<PostComposerFormData> = createValidationConfiguration<PostComposerFormData>();

	validationConfiguration
		.forCollection("images")
		.add(
			hasMaximumEntries(MAX_IMAGES_PER_POST)
		)
		.forItems(getClientId)
		.whenType((x) => x.kind === CommunityPostImageFormModelKind.New)
		.forScalar("image")
		// NOTE: indirect weirdness with wrapping the value in an OptionalModel, the reason being that the file validator
		// only operate on OptionalModel (at the time of writing)
		.add(
			inline(
				(value, c) => hasMaxSize(options.maxImageSize)
					.validate(
						{ isSet: true, value },
						c
					)
			),
			inline(
				(value, c) => isSupportedFileType(options.supportedImageTypes)
					.validate(
						{ isSet: true, value },
						c
					)
			)
		);

	const pollValidationConfiguration: IValidationConfiguration<PostComposerFormData["poll"]> = validationConfiguration
		.forObject("poll");

	const pollValueConfiguration: IValidationConfiguration<CommunityPollFormData> = pollValidationConfiguration
		.whenType(
			x => x.isSet
		)
		.forObject("value")
		.whenType(x => isNotNil(x));

	const answersConfiguration: ICollectionValidatorBuilder<CommunityPollAnswerFormData, readonly CommunityPollAnswerFormData[]> = pollValueConfiguration
		.forCollection("answers");

	answersConfiguration
		.add(
			hasMinimumEntries(MIN_POLL_ANSWERS_COUNT),
			hasMaximumEntries(MAX_POLL_ANSWERS_COUNT),
			inline(
				(answers: readonly CommunityPollAnswerFormData[]): InputValidationResult => {
					const answersSet: Set<string> = new Set<string>(
						answers.map(x => x.text)
					);

					if (answers.length !== answersSet.size) {
						return {
							kind: InputValidationResultKind.Invalid,
							errorMessage: localize("_ContainsADuplicateValue")
						};
					}

					return {
						kind: InputValidationResultKind.Valid
					};
				}
			)
		);

	answersConfiguration
		.forItems(getClientId)
		.forScalar("text")
		.add(
			isNotNullNorUndefined(),
			isNotEmpty(),
			doesNotExceedLength(100)
		);

	pollValueConfiguration
		.forScalar("question")
		.add(
			isNotNullNorUndefined(),
			isNotEmpty(),
			doesNotExceedLength(255)
		);

	validationConfiguration
		.forCollection("linkedDocuments")
		.add(
			hasMaximumEntries(MAX_LINKED_DOCUMENTS_COUNT)
		)
		.forItems(getClientId);

	return validationConfiguration;
}

export function createValidatorForNewPost(
	options: PostComposerValidationOptions
): IValidationConfiguration<PostComposerFormData> {
	const result: IValidationConfiguration<PostComposerFormData> = createValidationConfigurationForPost(options);

	result
		.when(
			x => x.images.length === 0
				&& (!x.poll.isSet || isNil(x.poll.value))
				&& (x.linkedDocuments.length === 0)
		)
		.forScalar("message")
		.add(
			isNotNullNorUndefined()
				.validateOn("formSubmit")
				.fixErrorOn("input"),
			isNotEmpty()
				.validateOn("formSubmit")
				.fixErrorOn("input")
		);

	return result;
}

export function createValidatorForEditPost(
	options: PostComposerValidationOptions,
	hasInitialPoll: boolean
): IValidationConfiguration<PostComposerFormData> {
	const result: IValidationConfiguration<PostComposerFormData> = createValidationConfigurationForPost(options);

	result
		.when(
			x => {
				const {
					images,
					linkedDocuments,
					poll
				} = x;

				const hasPoll: boolean = (poll.isSet && isNotNil(poll.value))
					|| (!poll.isSet && hasInitialPoll);

				const hasImages: boolean = images.length > 0;
				const hasLinkedDocuments: boolean = linkedDocuments.length > 0;

				return !hasPoll
					&& !hasImages
					&& !hasLinkedDocuments;
			}
		)
		.forScalar("message")
		.add(
			isNotNullNorUndefined()
				.validateOn("formSubmit")
				.fixErrorOn("input"),
			isNotEmpty()
				.validateOn("formSubmit")
				.fixErrorOn("input")
		);

	return result;
}
